/**
 * JSON-LD Schema builders.
 *
 * Each function returns a valid schema.org JSON-LD object.
 * Use with <JsonLd> component or useJsonLd hook.
 */

const SITE_NAME = 'הוט מועדון צרכנות';
const SITE_URL = typeof window !== 'undefined' ? window.location.origin : '';

// Google merchant listing limits
const MAX_PRODUCT_NAME_LENGTH = 150;
const MAX_PRODUCT_DESCRIPTION_LENGTH = 5000;

function truncate(value, maxLength) {
  const str = String(value || '').trim();
  if (str.length <= maxLength) return str;
  return `${str.slice(0, maxLength - 1).trimEnd()}…`;
}

/**
 * Organization schema - represents the site/brand.
 */
export function buildOrganizationSchema({ name, url, logo, description } = {}) {
  return {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: name || SITE_NAME,
    url: url || SITE_URL,
    ...(logo && { logo }),
    ...(description && { description }),
  };
}

/**
 * WebPage schema - generic page.
 */
export function buildWebPageSchema({ name, description, url }) {
  return {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    name,
    ...(description && { description }),
    url: url || (typeof window !== 'undefined' ? window.location.href : ''),
    isPartOf: {
      '@type': 'WebSite',
      name: SITE_NAME,
      url: SITE_URL,
    },
  };
}

/**
 * Product/Offer schema - for benefit/deal pages.
 * Falls back to WebPage when there is no price, since Google requires
 * Product to include offers, review or aggregateRating.
 */
export function buildOfferSchema({ name, description, url, image, price, priceCurrency = 'ILS', brand, category }) {
  if (!(Number(price) > 0) || !name?.trim()) {
    return buildWebPageSchema({ name, description, url });
  }

  return {
    '@context': 'https://schema.org',
    '@type': 'Product',
    name: truncate(name, MAX_PRODUCT_NAME_LENGTH),
    ...(description && { description: truncate(description, MAX_PRODUCT_DESCRIPTION_LENGTH) }),
    ...(url && { url }),
    ...(image && { image }),
    ...(brand && {
      brand: {
        '@type': 'Brand',
        name: brand,
      },
    }),
    ...(category && { category }),
    offers: {
      '@type': 'Offer',
      ...(url && { url }),
      price: String(price),
      priceCurrency,
      availability: 'https://schema.org/InStock',
      seller: {
        '@type': 'Organization',
        name: SITE_NAME,
        url: SITE_URL,
      },
    },
  };
}

/**
 * BreadcrumbList schema - for navigation breadcrumbs.
 */
export function buildBreadcrumbSchema(items) {
  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: items.map((item, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      name: item.name,
      item: item.url,
    })),
  };
}

/**
 * FAQ schema - for FAQ sections/pages.
 */
export function buildFAQSchema(questions) {
  return {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: questions.map(({ question, answer }) => ({
      '@type': 'Question',
      name: question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: answer,
      },
    })),
  };
}

/**
 * LocalBusiness schema - for supplier pages.
 */
export function buildLocalBusinessSchema({ name, description, url, image, address, telephone }) {
  return {
    '@context': 'https://schema.org',
    '@type': 'LocalBusiness',
    name,
    ...(description && { description }),
    ...(url && { url }),
    ...(image && { image }),
    ...(address && {
      address: {
        '@type': 'PostalAddress',
        ...(typeof address === 'string' ? { streetAddress: address } : address),
      },
    }),
    ...(telephone && { telephone }),
  };
}

/**
 * ItemList schema - for category/listing pages.
 */
export function buildItemListSchema({ name, description, items }) {
  return {
    '@context': 'https://schema.org',
    '@type': 'ItemList',
    name,
    ...(description && { description }),
    itemListElement: items.map((item, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      url: item.url,
      name: item.name,
    })),
  };
}
